import { useQuery, type QueryClient } from "@tanstack/react-query";

import { useRestClient } from "@/lib/restClient";
import type { DashboardKind, DashboardRecord } from "@/lib/dashboard/types";

export interface UseDashboardOptions {
  kind?: DashboardKind;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  /**
   * Reload the definition at this interval (ms), e.g. while a war-room screen
   * presents it, so a published change reaches the screen without a reload.
   */
  refetchInterval?: number | false;
}

export function dashboardQueryKey(id: string, kind: DashboardKind = "dashboard") {
  return kind === "template"
    ? ["dashboards", "admin", "template", id]
    : ["dashboards", "admin", id];
}

/** A dashboard already known from a cached listing, when the caller may see it. */
export function cachedDashboard(
  client: QueryClient,
  identifier: string,
  isVisible: (record: DashboardRecord) => boolean,
): DashboardRecord | undefined {
  const entries = client.getQueriesData<DashboardRecord | DashboardRecord[]>({
    queryKey: ["dashboards", "admin"],
  });
  for (const [key, data] of entries) {
    if (key.length !== 3 || !data) continue;
    const record = (Array.isArray(data) ? data : [data]).find(
      (candidate) =>
        (candidate.kind ?? "dashboard") === "dashboard" &&
        (candidate.id === identifier || candidate.slug === identifier) &&
        isVisible(candidate),
    );
    if (record) return record;
  }
  return undefined;
}

export function cachedDashboardTitle(
  client: QueryClient,
  identifier: string,
  isVisible: (record: DashboardRecord) => boolean,
): string | undefined {
  const record = cachedDashboard(client, identifier, isVisible);
  if (!record) return undefined;
  const identity = record.state === "draft" ? (record.draft ?? record) : record;
  return identity.config.title?.trim() || identity.name;
}

export function useDashboard(
  id: string,
  { refetchInterval = false, kind = "dashboard", ...refetchOptions }: UseDashboardOptions = {},
) {
  const apiFetch = useRestClient();

  return useQuery({
    ...refetchOptions,
    queryKey: dashboardQueryKey(id, kind),
    queryFn: () =>
      apiFetch<DashboardRecord>(
        `/api/dashboards/${encodeURIComponent(id)}${kind === "template" ? "?kind=template" : ""}`,
      ),
    staleTime: 30_000,
    enabled: Boolean(id),
    retry: refetchInterval === false ? undefined : false,
    refetchInterval: (query) => (query.state.error ? false : refetchInterval),
    // A screen nobody touches must keep following the definition.
    refetchIntervalInBackground: refetchInterval !== false,
  });
}
